import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import HandshakeIcon from '@mui/icons-material/Handshake';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import MobileScreenShareIcon from '@mui/icons-material/MobileScreenShare';
import SettingsIcon from '@mui/icons-material/Settings';
import { Badge, Button, Divider, IconButton, Menu, MenuItem, Skeleton, Typography } from '@mui/material';
import { useLoginContext } from '@tiffinger-thiel/appauth-react';
import { BaseEmployeeDTO, TenantCountryCodeEnum } from 'probonio-shared-ui/api';
import { useIsTenantOfCountry } from 'probonio-shared-ui/module/country/useIsWithinCountry';
import { useStaticLinks } from 'probonio-shared-ui/module/links/hooks';
import React, { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useNewDialogState } from '../../component/dialog';
import { SupportDialog } from '../../component/support';
import { useAppSelector } from '../../redux/hooks';
import { fontSizes, spacings } from '../../theme';
import { useWebAppUrl } from '../../util/use-web-app-url';
import { useAdminTenantAccessData } from '../tenant/TenantHierarchyHooks';
import { TenantSwitcher } from '../tenant/TenantSwitcher';
import { useTutorialContext } from '../tenant/Tutorial/tutorialContext';
import { LetterAvatar } from '../userManagement/LetterAvatar';
import { renderUserName } from '../userManagement/userFormatter';
import { ReferralDialog } from './ReferralDialog';
import { UserEditDialog } from './UserEditDialog';

const UserWithAvatar: React.FC<{
  employee?: Pick<BaseEmployeeDTO, 'firstName' | 'lastName' | 'mail'>;
  openUserEditDialog?: () => void;
}> = ({ employee, openUserEditDialog }) => {
  return employee ? (
    <>
      <LetterAvatar
        size={spacings.navbarAvatar}
        sx={{ marginRight: 0.5, fontSize: fontSizes.md, cursor: openUserEditDialog ? 'pointer' : 'inherit' }}
        firstName={employee?.firstName}
        lastName={employee?.lastName}
        mail={employee?.mail}
      />
      <Typography variant="h3" sx={{ marginRight: 1 }} noWrap>
        {renderUserName(employee)}
      </Typography>
    </>
  ) : (
    <>
      <Skeleton variant="circular" width={spacings.navbarAvatar} height={spacings.navbarAvatar} sx={{ marginRight: 0.5 }} />
      <Skeleton variant="text" width="3em" sx={{ marginRight: 0.5 }} />
      <Skeleton variant="text" width="4em" sx={{ marginRight: 1 }} />
    </>
  );
};

const UserMenuComp: React.FC = () => {
  const appUrl = useWebAppUrl();
  const { t } = useTranslation('navigationModule');
  const { login, isLoggedIn, isReady } = useLoginContext();
  const { user, ...me } = useAppSelector(state => state.me);
  const employment = user?.employments?.find(employment => employment.tenantId === me.currentTenantId);
  // When a admin switches to a tenant, in which he has no employment, the employment is undefined,
  // but we still want to show at least the mail
  const profileDisplay = employment ?? (user ? { mail: user.mail } : undefined);

  const handleLogin = useCallback(() => login(), [login]);

  const userEditDialog = useNewDialogState();
  const userMenu = useNewDialogState();
  const supportDialog = useNewDialogState();
  const referralCodeDialog = useNewDialogState();
  const links = useStaticLinks();
  const { data: adminTenantAccessData } = useAdminTenantAccessData(user, me.adminTenantAccessData);

  const isDETenant = useIsTenantOfCountry(TenantCountryCodeEnum.De);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const {
    drawerState: { handleOpen: handleOpenTutorial },
    steps: { openCount },
  } = useTutorialContext();

  const hasAvailableAdminTenants = adminTenantAccessData?.canSwitch ?? false;

  const handleOpenUserMenu = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
      userMenu.handleOpen();
    },
    [userMenu],
  );

  const handleCloseUserMenu = useCallback(() => {
    setAnchorEl(null);
    userMenu.dialogState.handleClose();
  }, [userMenu.dialogState]);

  const handleEditUser = useCallback(() => {
    handleCloseUserMenu();
    userEditDialog.handleOpen();
  }, [handleCloseUserMenu, userEditDialog]);

  return (
    <>
      {!isLoggedIn && isReady && (
        <Button variant="text" color="inherit" onClick={handleLogin}>
          {t('login')}
        </Button>
      )}
      {isLoggedIn && (
        <>
          <IconButton href={appUrl} sx={{ color: 'text.secondary' }} title={t('linkToApp')}>
            <MobileScreenShareIcon />
          </IconButton>

          <IconButton
            onClick={handleOpenTutorial}
            sx={{ color: 'text.secondary' }}
            title={t('tutorialModule:buttonOpenDrawer')}
            className="tutorial-opener"
            aria-label={t('tutorialModule:buttonOpenDrawer')}
          >
            <Badge badgeContent={openCount > 0 ? `${openCount}` : undefined} color="error">
              <LightbulbIcon />
            </Badge>
          </IconButton>

          {links?.MANUAL && (
            <IconButton href={links.MANUAL} target="_blank" sx={{ color: 'text.secondary' }} title={t('manual')}>
              <MenuBookIcon />
            </IconButton>
          )}
          <IconButton sx={{ color: 'text.secondary' }} title={t('menuItem.contactSupport')} onClick={supportDialog.handleOpen}>
            <ContactSupportIcon />
          </IconButton>

          {hasAvailableAdminTenants && <TenantSwitcher />}

          {isDETenant && (
            <IconButton sx={{ color: 'text.secondary' }} title={t('menuItem.referral')} onClick={referralCodeDialog.handleOpen}>
              <HandshakeIcon data-test-id="navbar-referral" />
            </IconButton>
          )}

          <IconButton component={Link} sx={{ color: 'text.secondary' }} title={t('menuItem.tenantSettings')} to="/settings">
            <SettingsIcon data-test-id="navbar-settings" />
          </IconButton>

          <IconButton sx={{ color: 'text.secondary' }} title={t('menuItem.account')} onClick={handleOpenUserMenu}>
            <AccountCircleIcon />
          </IconButton>
          <Menu anchorEl={anchorEl} open={userMenu.dialogState.isOpen} onClose={handleCloseUserMenu}>
            <MenuItem disabled sx={{ opacity: '1 !important' }}>
              <UserWithAvatar employee={profileDisplay} />
            </MenuItem>
            <Divider />
            {employment && <MenuItem onClick={handleEditUser}>{t('menuItem.userProfile')}</MenuItem>}
            <MenuItem component={Link} to="/logout" onClick={userMenu.dialogState.handleClose}>
              {t('logout')}
            </MenuItem>
          </Menu>

          <UserEditDialog dialogState={userEditDialog.dialogState} />
          <SupportDialog dialogState={supportDialog.dialogState} />
          <ReferralDialog dialogState={referralCodeDialog.dialogState} />
        </>
      )}
    </>
  );
};

export const UserMenu = React.memo(UserMenuComp);
